import { useCallback, useState } from "react";
import { Upload, X, FileText, Image as ImageIcon } from "lucide-react";

const ALLOWED_TYPES = [
  "image/jpeg", "image/png", "image/gif", "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/zip",
  "text/plain",
];

const ALLOWED_EXTS = ".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.zip,.txt";
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const DEFAULT_MAX_FILES = 5;

function fmtBytes(b: number) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

interface Props {
  files: File[];
  onChange: (files: File[]) => void;
  maxFiles?: number;
  disabled?: boolean;
}

export default function FileUploader({ files, onChange, maxFiles = DEFAULT_MAX_FILES, disabled }: Props) {
  const [dragOver, setDragOver] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  function mergeFiles(incoming: FileList | File[]) {
    const arr = Array.from(incoming);
    const errs: string[] = [];
    const valid: File[] = [];

    for (const f of arr) {
      if (!ALLOWED_TYPES.includes(f.type)) {
        errs.push(`${f.name}: unsupported type`);
        continue;
      }
      if (f.size > MAX_SIZE_BYTES) {
        errs.push(`${f.name}: exceeds 10 MB`);
        continue;
      }
      if (files.some((x) => x.name === f.name && x.size === f.size)) continue; // dedupe
      valid.push(f);
    }

    setErrors(errs);
    onChange([...files, ...valid].slice(0, maxFiles));
  }

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (!disabled) mergeFiles(e.dataTransfer.files);
    },
    [files, disabled],
  );

  function remove(idx: number) {
    setErrors([]);
    onChange(files.filter((_, i) => i !== idx));
  }

  const canAdd = files.length < maxFiles && !disabled;

  return (
    <div className="space-y-2">
      {/* Drop zone */}
      <label
        onDrop={onDrop}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        className={`flex flex-col items-center gap-2 py-5 px-4 border-2 border-dashed rounded-2xl transition-colors ${
          disabled
            ? "border-gray-100 bg-gray-50 cursor-not-allowed opacity-60"
            : dragOver
            ? "border-gray-900 bg-gray-50 cursor-copy"
            : canAdd
            ? "border-gray-200 hover:border-gray-400 hover:bg-gray-50 cursor-pointer"
            : "border-gray-100 bg-gray-50 cursor-not-allowed opacity-60"
        }`}
      >
        <Upload className="w-5 h-5 text-gray-400" />
        <div className="text-center">
          <p className="text-xs font-semibold text-gray-500">
            {canAdd
              ? <>Drop files or <span className="text-gray-900 font-black">click to browse</span></>
              : `Maximum ${maxFiles} files reached`}
          </p>
          <p className="text-[10px] text-gray-400 mt-0.5">
            JPG · PNG · GIF · PDF · DOC · XLS · ZIP — max 10 MB each
          </p>
        </div>
        <input
          type="file"
          multiple
          accept={ALLOWED_EXTS}
          disabled={!canAdd}
          className="hidden"
          onChange={(e) => e.target.files && mergeFiles(e.target.files)}
        />
      </label>

      {/* Errors */}
      {errors.length > 0 && (
        <ul className="space-y-1">
          {errors.map((err, i) => (
            <li key={i} className="text-[11px] text-red-600 font-medium">⚠ {err}</li>
          ))}
        </ul>
      )}

      {/* File previews */}
      {files.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {files.map((f, idx) => {
            const isImg = f.type.startsWith("image/");
            return (
              <div
                key={idx}
                className="relative flex items-center gap-2 p-2.5 bg-gray-50 border border-gray-200 rounded-xl group"
              >
                {isImg ? (
                  <img
                    src={URL.createObjectURL(f)}
                    alt={f.name}
                    className="w-9 h-9 rounded-lg object-cover shrink-0 border border-gray-200"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold text-gray-700 truncate leading-tight">{f.name}</p>
                  <p className="text-[10px] text-gray-400">{fmtBytes(f.size)}</p>
                </div>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => remove(idx)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gray-900 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
